/**
 * @override
 * King Living Blog component with lazy loading Rich Text links support
 */

import {useMemo} from 'react';
import PropTypes from 'prop-types';

import {Box, Skeleton, SkeletonText, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';

import BlogHeading from '@amblique/king-living/app/components/blog-heading';
import {RichTextContent} from '@amblique/contentful/app/components/rich-text-content';
import {useBlogLinks} from '@amblique/king-living/app/contentful/hooks/use-blog-links';
import {hasEmbeddedContent} from '@amblique/king-living/app/contentful/utils/rich-text';

/**
 * Format date to DD/MM/YYYY format
 */
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

export const Blog = ({component, isComplete = true, isFetching = false, ...props}) => {
  const styles = useMultiStyleConfig('contentful/blog', {component, isComplete, isFetching});

  const {
    title,
    author,
    datePublished,
    duration,
    description,
    blogContent,
    blogHeaderMedia,
    shareThisPostCollection,
  } = component;

  const hasContent = blogContent?.json;

  // Check if we need to fetch links
  const needsLinks = useMemo(() => {
    if (!hasContent || blogContent?.links) return false;

    return hasEmbeddedContent(blogContent.json);
  }, [hasContent, blogContent]);

  // Lazy load links only when needed
  const {links, isLoading: isLoadingLinks} = useBlogLinks(component.sys?.id, {
    enabled: needsLinks,
  });

  // Merge blogContent with fetched links
  const blogContentWithLinks = useMemo(() => {
    if (!hasContent) return blogContent;
    if (blogContent?.links) return blogContent;
    if (!links) return blogContent;

    return {
      ...blogContent,
      links,
    };
  }, [blogContent, links, hasContent]);

  const shareLinks = shareThisPostCollection?.items || [];

  // Show loading state
  const isLoading = (!isComplete && isFetching) || (needsLinks && isLoadingLinks);

  if (isLoading) {
    return (
      <Box {...styles.root} {...props}>
        <Skeleton height="400px" mb={8} width="100%" />
        <Skeleton height="48px" mb={4} width="80%" />
        <SkeletonText mb={4} noOfLines={1} width="40%" />
        <SkeletonText noOfLines={10} spacing={4} />
      </Box>
    );
  }

  return (
    <Box {...styles.root} {...props}>
      <BlogHeading
        author={author}
        datePublished={formatDate(datePublished)}
        description={description}
        readTime={duration}
        shareLinks={shareLinks}
        title={title}
      />

      {blogHeaderMedia?.url && (
        <Box {...styles.headerMedia}>
          <img alt={blogHeaderMedia.title || title} src={blogHeaderMedia.url} />
        </Box>
      )}

      {hasContent && (
        <Box {...styles.content}>
          <RichTextContent content={blogContentWithLinks} />
        </Box>
      )}
    </Box>
  );
};

Blog.displayName = 'Blog';
Blog.propTypes = {
  component: PropTypes.object.isRequired,
  isComplete: PropTypes.bool,
  isFetching: PropTypes.bool,
};
